import React, { useContext, ReactNode, useState, useEffect, useCallback } from 'react';
import { User } from 'firebase/app';
import * as Sentry from '@sentry/react';
import { firebase } from 'shared/lib';
import { AuthContext } from './context/AuthContext';
import { IAuthContext } from './interfaces/IAuthContext';
import { IAuthState } from './interfaces/IAuthState';
import { PersistedStateKeys } from 'shared/enums/PersistedStateKeys';
import { disable as disableDarkMode } from 'darkreader';
import useChurnZero from 'shared/hooks/useChurnZero';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import { persistor } from 'state/store';
import { LoginEvents } from '../TrackingProvider/EventNames';
import { env } from 'env';
import { resetCurrentClinicState } from 'state/currentClinic/currentClinicSlice';

export const useAuth = (): IAuthContext => useContext<IAuthContext>(AuthContext);

interface AuthProviderProps {
  children: ReactNode;
  value?: IAuthContext;
}

const AuthProvider = ({ children, value }: AuthProviderProps): JSX.Element => {
  const isImpersonating = useAppSelector((state) => state.currentClinic.isImpersonating);
  const dispatch = useAppDispatch();
  const [authState, setAuthState] = useState<IAuthState>({
    isInitialized: false,
    isAuthenticated: false,
  });
  const churnZero = useChurnZero();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | undefined>();
  const [isLoggingOut, setIsLoggingOut] = useState<boolean>(false);

  const initializeAuth = useCallback(() => {
    firebase.auth().onAuthStateChanged((user: User | null) => {
      if (user) {
        setAuthState({ isInitialized: true, isAuthenticated: true, user });
      } else {
        setAuthState({ isInitialized: true, isAuthenticated: false });
      }
    });
  }, []);

  const loginWithToken = useCallback(async (token: string, isImpersonating?: boolean): Promise<void> => {
    try {
      setError(undefined);
      setLoading(true);

      // Ensure this firebase session expires on refresh and only applies to this browser
      if (isImpersonating) {
        await firebase.auth().setPersistence('session');
      }

      await firebase.auth().signInWithCustomToken(token);
    } catch (e) {
      console.error(e);
      if (e instanceof Error) {
        setError(e.message);
      }
    }

    setLoading(false);
  }, []);

  const logout = useCallback(
    async (options: { keepLocalData?: boolean; suppressNavigation?: boolean } = {}): Promise<void> => {
      try {
        setError(undefined);
        setLoading(true);
        setIsLoggingOut(true);
        churnZero.trackEvent(LoginEvents.Logout);
        churnZero.stop();

        dispatch(resetCurrentClinicState());

        if (!options.keepLocalData) {
          deleteAllLocalDataFromBrowser();
          disableDarkMode();
          await persistor.purge();
        }

        await firebase.auth().signOut();

        if (!options.suppressNavigation) {
          window.location.href = `${env.REACT_APP_LOGIN_URL}/logout`;
        }
      } catch (e) {
        const error = e as { message: string };
        setError(error.message);
      } finally {
        setLoading(false);
        setIsLoggingOut(false);
      }
    },
    [churnZero, dispatch],
  );

  const getToken = useCallback(async (): Promise<string | null> => {
    try {
      const currentUser = firebase.auth().currentUser;
      const token = currentUser ? await currentUser.getIdToken(/* forceRefresh */ false) : undefined;

      if (token) {
        return token;
      }
      return null;
    } catch (e) {
      console.error('Error getting Firebase token', e);
      Sentry.captureException(e);
      logout();
      return null;
    }
  }, [logout]);

  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  return (
    <AuthContext.Provider
      value={
        value || {
          isInitialized: authState.isInitialized,
          isAuthenticated: authState.isAuthenticated,
          isLoggingOut,
          isImpersonating,
          user: authState.user,
          loading,
          error,
          loginWithToken,
          logout,
          getToken,
        }
      }
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;

/**
 * Allow clearing of local storage while persisting certain
 * keys. See whitelistPersistedStateKeys
 */
const whitelistPersistedStateKeys = [
  PersistedStateKeys.UserAppointmentGroupingsFilter,
  PersistedStateKeys.ChannelListFilters,
  PersistedStateKeys.ChannelListFilterSelections,
  PersistedStateKeys.AiRecapHideAppBanner
];
const clearLocalStorage = (): void => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const persistedState: Record<string, any> = {};
  whitelistPersistedStateKeys.forEach((key: string) => {
    persistedState[key] = localStorage.getItem(key);
  });

  localStorage.clear();

  Object.keys(persistedState).forEach((key: string) => {
    localStorage.setItem(key, persistedState[key]);
  });
};

const deleteAllLocalDataFromBrowser = (): void => {
  // clear local select storage
  clearLocalStorage();

  // clear session storage
  sessionStorage.clear();

  // expire all cookies
  const cookies = document.cookie.split(';');
  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i];
    const eqPos = cookie.indexOf('=');
    const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
    document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT';
  }
};
